import { GameState }    from '../../core/GameState.js';
import { formatMoney, escapeHtml } from '../../utils/formatting.js';
import { MILESTONES, COMPANY_COLOR_PALETTE, COMPANY_NAME_MAX_LENGTH } from '../../config/constants.js';
import { showAlert }    from '../components/dialogs.js';
import { serverIntent } from '../../core/serverIntent.js';

/** Current progress value for a milestone's condition.type. */
function _currentValueFor(type, { player, ships, netWorth }) {
  switch (type) {
    case 'deliveries':   return player?.statistics?.totalDeliveries || 0;
    case 'ships':        return ships.length;
    case 'balance':      return player?.money || 0;
    case 'portsVisited': return player?.statistics?.portsVisited?.size || 0;
    case 'netWorth':     return netWorth;
    default:             return 0;
  }
}

const _MONEY_CONDITION_TYPES = new Set(['balance', 'netWorth']);

/**
 * Finance & Company tab — two sub-tabs (same pattern as ResearchPanel.js's
 * Research/Permits split):
 *   - Finance: balance, fleet value, net worth, revenue/expenses/deliveries
 *   - Company: identity (name/color, logo proposed for later), ports
 *     visited, ships owned, milestones — these aren't really "finance",
 *     they're about the company itself.
 */
export class FinancePanel {
  constructor(uiManager) {
    this.ui = uiManager;
    this._activeSubTab = 'finance'; // 'finance' | 'company'
  }

  render() {
    const container = document.querySelector('.panel-content');
    const player    = GameState.player;
    if (!player) { container.innerHTML = '<p>No player data.</p>'; return; }

    const fActive = this._activeSubTab === 'finance' ? 'active' : '';
    const cActive = this._activeSubTab === 'company' ? 'active' : '';

    container.innerHTML = `
      <div class="research-subtabs">
        <button class="rtab ${fActive}" data-rtab="finance">Finance</button>
        <button class="rtab ${cActive}" data-rtab="company">Company</button>
      </div>
      <div class="research-subtab-content"></div>`;

    container.querySelectorAll('.rtab').forEach(btn => {
      btn.addEventListener('click', () => {
        this._activeSubTab = btn.dataset.rtab;
        this.render();
      });
    });

    const content = container.querySelector('.research-subtab-content');
    if (this._activeSubTab === 'finance') {
      this._renderFinance(content, player);
    } else {
      this._renderCompany(content, player);
    }
  }

  // ── Finance sub-tab ─────────────────────────────────────
  _renderFinance(el, player) {
    const ships      = GameState.getAllShips();
    const fleetValue = ships.reduce((sum, s) => sum + (s.type?.basePurchasePrice || 0) * 0.5, 0);
    const netWorth   = player.money + fleetValue;

    el.innerHTML = `
      <div class="finance-grid">
        <div class="finance-card">
          <h5>Balance</h5>
          <span class="finance-big">${formatMoney(player.money)}</span>
        </div>
        <div class="finance-card">
          <h5>Fleet Value</h5>
          <span class="finance-big">${formatMoney(fleetValue)}</span>
        </div>
        <div class="finance-card">
          <h5>Net Worth</h5>
          <span class="finance-big">${formatMoney(netWorth)}</span>
        </div>
      </div>

      <div class="finance-stats">
        <h5>Trading Performance</h5>
        <table class="stats-table">
          <tr><td>Total Revenue</td>   <td>${formatMoney(player.statistics.totalRevenue)}</td></tr>
          <tr><td>Total Expenses</td>  <td>${formatMoney(player.statistics.totalExpenses)}</td></tr>
          <tr><td>Deliveries</td>      <td>${player.statistics.totalDeliveries}</td></tr>
        </table>
      </div>`;
  }

  // ── Company sub-tab ─────────────────────────────────────
  _renderCompany(el, player) {
    const ships = GameState.getAllShips();
    const fleetValue = ships.reduce((sum, s) => sum + (s.type?.basePurchasePrice || 0) * 0.5, 0);
    const netWorth   = player.money + fleetValue;

    el.innerHTML = `
      <div class="finance-milestones">
        <h5>Milestones</h5>
        ${this._renderMilestones(player, ships, netWorth)}
      </div>

      <div class="company-identity">
        <h5>Company Identity</h5>
        <div class="route-field">
          <label>Company name</label>
          <div class="company-name-row">
            <input type="text" id="fp-company-name" value="${escapeHtml(player.companyName)}" maxlength="${COMPANY_NAME_MAX_LENGTH}">
            <button type="button" class="btn btn-sm" id="fp-company-name-save">Save</button>
          </div>
        </div>
        <div class="menu-color-picker">
          <span class="menu-color-picker-label">Company color (your ships on the map)</span>
          <div class="menu-color-swatches" id="fp-company-swatches">
            ${COMPANY_COLOR_PALETTE.map(c => `
              <button type="button" class="menu-color-swatch${c === player.companyColor ? ' active' : ''}"
                      data-color="${c}" style="background:${c}" title="${c}"></button>`).join('')}
          </div>
        </div>
        <div class="company-logo-placeholder" title="Proposed for a future update">
          <span>Company logo</span>
          <small>Coming soon</small>
        </div>
      </div>

      <div class="finance-stats">
        <h5>Company Statistics</h5>
        <table class="stats-table">
          <tr><td>Ports Visited</td> <td>${player.statistics.portsVisited.size}</td></tr>
          <tr><td>Ships Owned</td>   <td>${ships.length}</td></tr>
        </table>
      </div>`;

    document.getElementById('fp-company-name-save').addEventListener('click', () => this._saveCompanyName());

    el.querySelectorAll('#fp-company-swatches .menu-color-swatch').forEach(btn => {
      btn.addEventListener('click', () => this._saveCompanyColor(btn.dataset.color));
    });

    el.querySelectorAll('.milestone-row').forEach(row => {
      row.addEventListener('click', () => {
        const m = MILESTONES.find(x => x.id === row.dataset.milestone);
        if (m) showAlert(m.description, 'info');
      });
    });
  }

  async _saveCompanyName() {
    const input = document.getElementById('fp-company-name');
    const companyName = input.value.trim();
    if (!companyName) return;
    const result = await serverIntent('player/company-name', { companyName });
    if (result.success) {
      // The server also unicasts player:companyNameChanged back over the
      // same WS connection (updates GameState.player.companyName + the HUD)
      // — this just reflects it immediately rather than waiting on that
      // round-trip, same pattern as the company-color save below.
      if (GameState.player) GameState.player.companyName = companyName;
      this.render();
    }
  }

  async _saveCompanyColor(color) {
    const result = await serverIntent('player/company-color', { color });
    if (result.success) {
      if (GameState.player) GameState.player.companyColor = color;
      this.render();
    }
  }

  /** @param {Object} player @param {Array} ships @param {number} netWorth */
  _renderMilestones(player, ships, netWorth) {
    const earned = player?.statistics?.milestonesEarned || new Set();
    return MILESTONES.map(m => {
      const done = earned.has(m.id);
      const isMoney = _MONEY_CONDITION_TYPES.has(m.condition.type);
      const current = _currentValueFor(m.condition.type, { player, ships, netWorth });
      const pct = Math.max(0, Math.min(100, Math.round((current / m.condition.value) * 100)));
      const fmt = (n) => isMoney ? formatMoney(n) : n.toLocaleString();

      return `<div class="milestone-row ${done ? 'done' : 'pending'}" data-milestone="${m.id}" title="${escapeHtml(m.description)}">
        <div class="milestone-row-main">
          <span>${done ? '✓' : '○'} ${m.name}</span>
          <span>${formatMoney(m.reward)}</span>
        </div>
        ${!done ? `
          <div class="progress-bar-track milestone-progress"><div class="progress-bar-fill" style="width:${pct}%"></div></div>
          <small class="milestone-progress-label">${fmt(current)} / ${fmt(m.condition.value)}</small>
        ` : ''}
      </div>`;
    }).join('');
  }
}
